/**
 * A small syntax highlighter for code blocks (```js … ```). It splits code
 * into tokens (keywords, strings, comments…) that are rendered as React text
 * spans: no HTML strings, so nothing in a message can become markup. One pass,
 * linear in the code's length, so hostile input can't slow it down.
 */

export type TokenKind = 'keyword' | 'string' | 'comment' | 'number' | 'function' | 'type' | 'tag' | 'attr' | 'property' | 'operator';

export interface Token {
  text: string;
  kind?: TokenKind;
}

type Rule = [RegExp, TokenKind | ((match: string) => TokenKind | undefined) | undefined];

const words = (list: string) => new Set(list.split(' '));

const JS_KEYWORDS = words(
  'abstract as async await break case catch class const continue debugger declare default delete do else enum export extends false finally for from function get if implements import in instanceof interface keyof let new null of private protected public readonly return satisfies set static super switch this throw true try type typeof undefined var void while with yield',
);
const PY_KEYWORDS = words('and as assert async await break class continue def del elif else except False finally for from global if import in is lambda None nonlocal not or pass raise return self True try while with yield match case');
const C_KEYWORDS = words(
  'auto bool break case catch char class const continue default delete do double else enum extern false final float fn for func go goto if impl import int interface let long loop match mod mut namespace new nil null package private protected pub public return self short signed sizeof static string struct super switch this throw true try type typedef union unsigned use using var virtual void volatile while defer chan map range select local function then end elseif repeat until nil not and or echo',
);
const SQL_KEYWORDS = words(
  'select from where and or not insert into values update set delete create table drop alter add index primary key foreign references join left right inner outer on group by order having limit offset as distinct union all null is in like between case when then else end exists default returning with asc desc count sum avg min max',
);
const SH_KEYWORDS = words('if then else elif fi for while do done case esac in function return export local echo cd sudo npm npx git docker ssh cat ls rm cp mv mkdir grep sed curl');

const LANGS: Record<string, string> = {
  js: 'js', javascript: 'js', jsx: 'js', ts: 'js', typescript: 'js', tsx: 'js', mjs: 'js', cjs: 'js',
  py: 'py', python: 'py',
  json: 'json', jsonc: 'json',
  css: 'css', scss: 'css', less: 'css',
  html: 'html', xml: 'html', svg: 'html', vue: 'html',
  sh: 'sh', bash: 'sh', shell: 'sh', zsh: 'sh', console: 'sh', powershell: 'sh', ps1: 'sh',
  sql: 'sql', postgres: 'sql', mysql: 'sql',
  c: 'c', h: 'c', cpp: 'c', 'c++': 'c', cs: 'c', csharp: 'c', java: 'c', kotlin: 'c', go: 'c', rust: 'c', rs: 'c', swift: 'c', php: 'c', lua: 'c', dart: 'c',
};

const NUMBER: Rule = [/\b(?:0[xX][\da-fA-F_]+|\d[\d_]*(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/y, 'number'];
const DQ: Rule = [/"(?:[^"\\\n]|\\.)*"?/y, 'string'];
const SQ: Rule = [/'(?:[^'\\\n]|\\.)*'?/y, 'string'];

function identifier(keywords: Set<string>, caseInsensitive = false): Rule {
  return [
    /[A-Za-z_$][\w$]*(?=\s*\()|[A-Za-z_$][\w$]*/y,
    (word) => {
      if (keywords.has(caseInsensitive ? word.toLowerCase() : word)) return 'keyword';
      if (/^[A-Z][a-z0-9]/.test(word)) return 'type';
      return undefined;
    },
  ];
}

const RULES: Record<string, Rule[]> = {
  js: [
    [/\/\/[^\n]*/y, 'comment'],
    [/\/\*[\s\S]*?(?:\*\/|$)/y, 'comment'],
    DQ,
    SQ,
    [/`(?:[^`\\]|\\.)*`?/y, 'string'],
    NUMBER,
    [/[A-Za-z_$][\w$]*(?=\s*\()/y, (w) => (JS_KEYWORDS.has(w) ? 'keyword' : 'function')],
    identifier(JS_KEYWORDS),
    [/=>|[=!]==?|[-+*/%&|^<>]=?|\?\?|\?\./y, 'operator'],
  ],
  py: [
    [/#[^\n]*/y, 'comment'],
    [/(?:[rbfRBF]{0,2})"""[\s\S]*?(?:"""|$)/y, 'string'],
    [/(?:[rbfRBF]{0,2})'''[\s\S]*?(?:'''|$)/y, 'string'],
    [/[rbfRBF]{0,2}"(?:[^"\\\n]|\\.)*"?/y, 'string'],
    [/[rbfRBF]{0,2}'(?:[^'\\\n]|\\.)*'?/y, 'string'],
    NUMBER,
    [/@[\w.]+/y, 'function'],
    [/[A-Za-z_]\w*(?=\s*\()/y, (w) => (PY_KEYWORDS.has(w) ? 'keyword' : 'function')],
    identifier(PY_KEYWORDS),
  ],
  json: [[/"(?:[^"\\\n]|\\.)*"(?=\s*:)/y, 'property'], DQ, NUMBER, [/\b(?:true|false|null)\b/y, 'keyword']],
  css: [
    [/\/\*[\s\S]*?(?:\*\/|$)/y, 'comment'],
    DQ,
    SQ,
    [/#[\da-fA-F]{3,8}\b/y, 'number'],
    [/-?\d*\.?\d+(?:px|em|rem|%|vh|vw|s|ms|deg|fr)?\b/y, 'number'],
    [/[\w-]+(?=\s*:)/y, 'property'],
    [/[.#][\w-]+/y, 'type'],
    [/@[\w-]+/y, 'keyword'],
  ],
  html: [
    [/<!--[\s\S]*?(?:-->|$)/y, 'comment'],
    [/<\/?[\w:-]+/y, 'tag'],
    [/\/?>/y, 'tag'],
    [/[\w:-]+(?==)/y, 'attr'],
    DQ,
    SQ,
  ],
  sh: [[/#[^\n]*/y, 'comment'], DQ, SQ, [/\$\{?[\w@#?*!-]+\}?/y, 'property'], [/(?:^|(?<=\s))--?[\w-]+/y, 'attr'], NUMBER, identifier(SH_KEYWORDS)],
  sql: [[/--[^\n]*/y, 'comment'], [/\/\*[\s\S]*?(?:\*\/|$)/y, 'comment'], SQ, DQ, NUMBER, identifier(SQL_KEYWORDS, true)],
  c: [
    [/\/\/[^\n]*/y, 'comment'],
    [/\/\*[\s\S]*?(?:\*\/|$)/y, 'comment'],
    [/#\s*\w+/y, 'keyword'],
    DQ,
    SQ,
    NUMBER,
    [/[A-Za-z_]\w*(?=\s*\()/y, (w) => (C_KEYWORDS.has(w) ? 'keyword' : 'function')],
    identifier(C_KEYWORDS),
  ],
};

/** The language a code block's tag names, if we highlight it. */
export function highlightLanguage(lang: string | null | undefined): string | null {
  return lang ? (LANGS[lang.toLowerCase()] ?? null) : null;
}

/** Splits code into tokens; plain text has no kind. Unknown languages come back as one plain token. */
export function highlight(code: string, lang: string | null | undefined): Token[] {
  const language = highlightLanguage(lang);
  if (!language) return [{ text: code }];
  const rules = RULES[language]!;
  const tokens: Token[] = [];
  let plain = '';
  let i = 0;
  const flush = () => {
    if (plain) tokens.push({ text: plain });
    plain = '';
  };
  outer: while (i < code.length) {
    for (const [re, kind] of rules) {
      re.lastIndex = i;
      const match = re.exec(code);
      if (match && match[0].length > 0) {
        const text = match[0];
        const resolved = typeof kind === 'function' ? kind(text) : kind;
        if (resolved) {
          flush();
          tokens.push({ text, kind: resolved });
        } else plain += text;
        i += text.length;
        continue outer;
      }
    }
    plain += code[i];
    i++;
  }
  flush();
  return tokens;
}
